import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { Layout } from '../../components/layout/Layout';
import { 
  Tablet, ArrowLeft, Edit, Building2, MapPin, Calendar, User,
  Smartphone, HardDrive, Cpu, Phone, CheckCircle, XCircle
} from 'lucide-react';
import { formatDateOnlyARG } from '../../lib/utils';

const TabletDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUpdate } = usePermissions('micro', 'tablets');
  
  const [tablet, setTablet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTablet = async () => {
      try {
        const response = await api.get(`/assets/tablets/${id}`);
        setTablet(response.data);
      } catch (error) {
        toast.error('Error al cargar la tablet');
        navigate('/micro/tablets');
      } finally {
        setLoading(false);
      }
    };
    fetchTablet();
  }, [id, navigate]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!tablet) return null;

  const getStatusBadge = () => {
    if (!tablet.status_name) {
      return (
        <span className="badge-base flex items-center gap-1.5 badge-yellow">
          <span className="status-dot w-2 h-2 bg-yellow-500 rounded-full" />
          Sin estado
        </span>
      );
    }
    if (tablet.status_name.toLowerCase().includes('activo')) {
      return (
        <span className="badge-base flex items-center gap-1.5 badge-green">
          <span className="status-dot w-2 h-2 status-online" />
          {tablet.status_name}
        </span>
      );
    }
    return (
      <span className="badge-base flex items-center gap-1.5 badge-red">
        <span className="status-dot w-2 h-2 status-offline" />
        {tablet.status_name}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/micro/tablets')}>
              <ArrowLeft className="h-5 w-5" strokeWidth={1.5} />
            </Button>
            <div className="flex items-center gap-3">
              <Tablet className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="text-2xl font-semibold">{tablet.asset_tag || tablet.serial_number || `Tablet #${tablet.id}`}</h1>
                <p className="text-sm text-muted-foreground">{tablet.brand_name} {tablet.modelo}</p>
              </div>
            </div>
          </div>
          {canUpdate && (
            <Button variant="outline" onClick={() => navigate(`/micro/tablets?edit=${tablet.id}`)}>
              <Edit className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Editar
            </Button>
          )}
        </div>

        {/* Status Card */}
        <Card className="nerve-card">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center gap-8">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Estado</p>
                {getStatusBadge()}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">SO</p>
                <p className="text-lg font-medium">{tablet.os_name?.trim() || '-'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Almacenamiento</p>
                <p className="text-lg font-medium">{tablet.storage_gb ? `${tablet.storage_gb} GB` : '-'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">RAM</p>
                <p className="text-lg font-medium">{tablet.ram_gb ? `${tablet.ram_gb} GB` : '-'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Usuario</p>
                <p className="text-lg font-medium">{tablet.assigned_user_name || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* General Info */}
          <Card className="nerve-card">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                <Tablet className="h-4 w-4" strokeWidth={1.5} />
                Información General
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Asset Tag</span>
                  <span className="font-mono">{tablet.asset_tag || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Serial Number</span>
                  <span className="font-mono">{tablet.serial_number || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Marca</span>
                  <span>{tablet.brand_name || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Modelo</span>
                  <span>{tablet.modelo || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Sistema Operativo</span>
                  <span>{tablet.os_name?.trim() || '-'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">IP Address</span>
                  <span className="font-mono">{tablet.ip_address || '-'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location Info */}
          <Card className="nerve-card">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                <MapPin className="h-4 w-4" strokeWidth={1.5} />
                Ubicación y Organización
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Empresa</span>
                  <span>{tablet.empresa_name || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Site</span>
                  <span>{tablet.site_name || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Ubicación</span>
                  <span>{tablet.location_name || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Usuario Asignado</span>
                  <span>{tablet.assigned_user_name || '-'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Proveedor</span>
                  <span>{tablet.supplier_name || '-'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Connectivity Info */}
          <Card className="nerve-card">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                <Phone className="h-4 w-4" strokeWidth={1.5} />
                Conectividad
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">IMEI</span>
                  <span className="font-mono">{tablet.imei || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Número de Teléfono</span>
                  <span className="font-mono">{tablet.phone_number || '-'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">IP Address</span>
                  <span className="font-mono">{tablet.ip_address || '-'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Purchase Info */}
          <Card className="nerve-card">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                <Calendar className="h-4 w-4" strokeWidth={1.5} />
                Compra y Garantía
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Fecha de Compra</span>
                  <span>{tablet.purchase_date ? formatDateOnlyARG(tablet.purchase_date) : '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Fin de Garantía</span>
                  <span>{tablet.warranty_end ? formatDateOnlyARG(tablet.warranty_end) : '-'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Proveedor</span>
                  <span>{tablet.supplier_name || '-'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Comments */}
        {tablet.comments && (
          <Card className="nerve-card">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Comentarios
              </h3>
              <p className="text-muted-foreground">{tablet.comments}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default TabletDetail;
